import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Grid,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Pagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Approval } from '../types';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';
import Header from '../components/Header';

const ApprovalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, handleUnauthorized } = useAuth();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch approvals from Strapi API with pagination
  useEffect(() => {
    const fetchAllApprovals = async () => {
      try {
        setLoading(true);
        setError(null);

        let allApprovals: Approval[] = [];
        let currentPage = 1;
        let totalPages = 1;

        // Fetch all pages
        while (currentPage <= totalPages) {
          const url = `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.LIST}?pagination[page]=${currentPage}&pagination[pageSize]=25&populate[itemrequests][populate]=*&populate[users_permissions_user][populate]=*`;
          console.log('Fetching from URL:', url);
          console.log('Token present:', !!token);
          
          const response = await fetch(
            url,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            }
          );
          
          console.log('Response status:', response.status);

          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              handleUnauthorized();
              return;
            }
            throw new Error(`Failed to fetch approvals: ${response.statusText}`);
          }

          const data = await response.json();
          console.log(`Approval API Response (Page ${currentPage}):`, data);

          // Get total pages from pagination meta
          if (data.meta?.pagination) {
            totalPages = data.meta.pagination.pageCount;
          }

          // Transform Strapi v5 response to match Approval interface
          const transformedApprovals: Approval[] = (data.data || []).map(
            (item: any) => {
              const itemrequests = item.itemrequests || [];
              
              return {
                id: item.id.toString(),
                documentId: item.documentId ? item.documentId.toString() : undefined,
                title: item.title || 'Untitled Request',
                items: itemrequests.map((ir: any) => {
                  return {
                    itemName: ir.item_estimate.item ||  'Unknown Item',
                    quantity: ir.quantity || 0,
                    rate: ir.unitPrice || 0,
                    totalAmount: ir.totalPrice || (ir.quantity || 0) * (ir.unitPrice || 0),
                    comment: ir.comment || undefined,
                  };
                }),
                totalAmount:
                  itemrequests.reduce(
                    (sum: number, ir: any) =>
                      sum + (ir.totalPrice || 0),
                    0
                  ) || 0,
                status: (
                  item.approvalStatus || 'Pending'
                ).toLowerCase() as 'pending' | 'approved' | 'rejected' | 'returned',
                requestedBy: `${item.users_permissions_user?.email}(${item.users_permissions_user?.username})` || 'Unknown User',
                createdAt: item.createdAt
                  ? new Date(item.createdAt).toLocaleDateString()
                  : 'N/A',
                approvers: item.approvals || [],
              };
            }
          );

          allApprovals = [...allApprovals, ...transformedApprovals];
          currentPage++;
        }

        console.log(`Total Approvals Fetched: ${allApprovals.length}`, allApprovals);
        setApprovals(allApprovals);
      } catch (err) {
        console.error('Error fetching approvals:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to fetch approvals'
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchAllApprovals();
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleRowClick = (id: string) => {
    navigate(`/approval/${id}`);
  };

  // Check if current user is a requestor
  const isRequestor = user?.role?.name === "Requester";

  // Filter approvals based on search term and sort by most recent
  const filteredApprovals = useMemo(() => {
    const filtered = approvals.filter((approval) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        approval.title.toLowerCase().includes(searchLower) ||
        approval.requestedBy.toLowerCase().includes(searchLower) ||
        approval.status.toLowerCase().includes(searchLower)
      );
    });
    
    // Sort by creation date in descending order (most recent first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [approvals, searchTerm]);

  // Paginate filtered approvals
  const totalPages = Math.ceil(filteredApprovals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedApprovals = filteredApprovals.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when search term changes
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
              Approval Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
              View and manage pending approvals
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip
            title={isRequestor ? '' : 'You do not have the Requestor role. Only users with the Requestor role can raise approval requests.'}
            disableHoverListener={isRequestor}
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/request')}
                disabled={!isRequestor}
              >
                Request Approval
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Box sx={{ mb: 3, width: '100%' }}>
            <TextField
              fullWidth
              placeholder="Search by title, requester name, or status..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            />
          </Box>

          {filteredApprovals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                {searchTerm ? 'No approvals found matching your search' : 'No approvals found'}
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {paginatedApprovals.map((approval) => (
          <Grid item xs={12} sm={6} lg={4} key={approval.id}>
            <Card
              onClick={() => handleRowClick(approval.documentId)}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, md: 2 } }}>
                {/* Header Row: Title and Status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' }, flex: 1, wordBreak: 'break-word', lineHeight: 1.3 }}>
                    {approval.title}
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: { xs: 0.5, md: 1 },
                      px: { xs: 1, md: 2 },
                      py: { xs: 0.5, md: 0.75 },
                      borderRadius: 1,
                      backgroundColor:
                        approval.status === 'approved'
                          ? '#e8f5e9'
                          : approval.status === 'rejected'
                          ? '#ffebee'
                          : approval.status === 'returned'
                          ? '#e1f5fe'
                          : '#fffde7',
                      color:
                        approval.status === 'approved'
                          ? '#2e7d32'
                          : approval.status === 'rejected'
                          ? '#c62828'
                          : approval.status === 'returned'
                          ? '#01579b'
                          : '#f57f17',
                      border: '1.5px solid',
                      borderColor:
                        approval.status === 'approved'
                          ? '#2e7d32'
                          : approval.status === 'rejected'
                          ? '#c62828'
                          : approval.status === 'returned'
                          ? '#01579b'
                          : '#f57f17',
                      fontWeight: 500,
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      flexShrink: 0,
                    }}
                  >
                    {approval.status === 'approved' ? (
                      <CheckCircleIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                    ) : approval.status === 'rejected' ? (
                      <CancelIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                    ) : approval.status === 'returned' ? (
                      <HelpOutlineIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                    ) : approval.status === 'pending' ? (
                      <AccessTimeIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                    ) : null}
                    <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                    </Typography>
                  </Box>
                </Box>

                {/* Date Row */}
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, display: 'block', mb: 2 }}>
                  {approval.createdAt}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Items ({approval.items.length})
                  </Typography>
                  <Stack spacing={1}>
                    {approval.items.map((item, idx) => (
                      <Box key={idx} sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: '500', fontSize: { xs: '0.813rem', md: '0.875rem' }, flex: 1, wordBreak: 'break-word' }}>
                            {item.itemName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '0.813rem', md: '0.875rem' }, whiteSpace: 'nowrap' }}>
                            ₹{item.totalAmount.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                            Qty: {item.quantity}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                            Rate: ₹{item.rate.toFixed(2)}
                          </Typography>
                        </Box>
                        {item.comment && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            💬 {item.comment}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: { xs: '0.813rem', md: '1rem' } }}>
                      Total Amount
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '0.813rem', md: '1rem' } }}>
                      ₹{approval.totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                    Requested by: {approval.requestedBy}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions sx={{ pt: 1, justifyContent: 'flex-end', p: { xs: 1, md: 2 } }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleRowClick(approval.documentId)}
                >
                  View Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
              </Grid>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    sx={{ '& .MuiPaginationItem-root': { fontSize: { xs: '0.75rem', md: '0.875rem' } } }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      </Container>
    </Box>
  );
};

export default ApprovalDashboard;
